export function toUpperCaseFirstLetter(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Recursively merges the properties from `source` into `target`, modifying `target` in place.
 * For each property in `source`:
 * - If the property is a non-null, non-array object, it recursively merges into the corresponding property in `target`.
 * - Otherwise, it assigns the value directly to `target`.
 * Note: This function assumes that `target` has the necessary structure for nested objects; if a nested object exists in `source` but the corresponding property in `target` is undefined, it will throw an error.
 *
 * @template T The type of the target object.
 * @param {T} target The object to merge into.
 * @param {Partial<T>} source The object to merge from.
 * @returns {T} The modified target object.
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  for (const key in source) {
    if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        target[key] = deepMerge(target[key], source[key]);
    } else {
        target[key] = source[key];
    }
  }

  return target;
}

/**
 * Creates a throttled function that only invokes `func` at most once per every `limit` milliseconds.
 * The throttled function comes with a leading invocation, meaning it will execute on the leading edge of the timeout.
 * Subsequent calls within the throttle period are ignored.
 *
 * @template T - The type of the function to throttle, which extends a function taking any arguments and returning void.
 * @param {T} func - The function to throttle.
 * @param {number} limit - The number of milliseconds to throttle invocations to.
 * @returns {(...args: Parameters<T>) => void} - Returns the new throttled function.
 */
export function throttle<T extends (...args: unknown[]) => void>(func: T, limit: number): (...args: Parameters<T>) => void {
  let inThrottle = false;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
      return func(...args);
    }
  };
}
